import { Component, OnInit, ViewEncapsulation, Input, SimpleChanges, OnChanges, OnDestroy, Output, EventEmitter, SecurityContext } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { fadeInOut } from '../../shared/helpers/animations';
import { StorageManagerService } from '../../shared/services/storage-manager.service';
import { IContactDetail, IEntity, ILLBusinessCategory } from '../../shared/models/index';
import { CompanyContactService, AuthorizationService } from '../../shared/services/index';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { Subscription } from 'rxjs';
import { Utilities } from '../../shared/services/utilities';
import { phoneNumberValidator } from '../../shared/helpers/phone-number.validator';
import { LCRFormComponent, ERFormComponent } from '../index';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-contact-detail',
    templateUrl: './contact-detail.component.html',
    styleUrls: ['./contact-detail.component.scss'],
    animations: [fadeInOut],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ContactDetailComponent implements OnInit, OnChanges, OnDestroy {
    @Input() contactRevisionId: string;
    @Input() contactData: IContactDetail;
    @Input() companyType: string;

    @Input() parentComponentInput: LCRFormComponent | ERFormComponent;

    @Output() cancelButtonClickedOutput = new EventEmitter<any>();
    @Output() contactOutput = new EventEmitter<any>();
    @Output() contactRetrievedOutput = new EventEmitter<any>();


    sub: Subscription;
    public contact: IContactDetail;
    public contactForm: UntypedFormGroup;
    public pageTitle: string;
    countries: IEntity[];
    regions: IEntity[];
    businessCategories: ILLBusinessCategory[];
    // Use with the generic validation message class
    displayMessage: { [key: string]: string } = {};
    errorMessage: string;
    selectedBusinessCategoryId: string;
    selectedCountryId: string;
    selectedRegionId: string;
    regionFromDrp: string;
    buttonText: string;
    contactId: string;
    submitted = false;
    bFirstTime = true;
    loading = false;
    isFormDraftFlag = false;
    version: string;
    formStatus: string;
    COUNTRY_USA = '185'; // Country code for United States
    countryRegion: any;
    country: any;
    countryId: any;
    businessCategoryId: any;
    bFromCompanyPage: boolean;
    bFromCompanyPageEditLink = false;
    bSavedContact: boolean;
    bIsDraftMode: boolean;
    bHasPermission = false;
    bHasDeletePermission = false;

    // Company Types
    COMPANY_TYPE_EST = 'Establishment_Owned';
    COMPANY_TYPE_IMPORTER = 'Importer';
    COMPANY_TYPE_LABELER = 'Labeler';
    COMPANY_TYPE_REGISTRANT = 'Registrant';
    COMPANY_TYPE_US_AGENT = 'US_Agent';
    // maps the appropriate column to fields property
    public businessCategoryFields: Object = {
        text: 'label',
        value: 'categoryId'
    };
    public countryFields: Object = {
        text: 'label',
        value: 'id'
    };
    public stateFields: Object = {
        text: 'regionAndAbbreviation',
        value: 'regionAndAbbreviation'
    };

    // set the placeholder to DropDownList input elements
    public businessCategoryWaterMark = 'Select a Business Category';
    public stateWaterMark = 'Select a State/Region';
    public countryWaterMark = 'Select a Country';

    formErrors = {
        email: '',
        firstName: '',
        lastName: '',
        isActive: '',
        address: '',
        city: '',
        countryRegion: '',
        zip: '',
        countryId: '',
        businessCategoryId: '',
        status: '',
        phoneExt: '',
        areaCode: '',
        dialCode: '',
        phoneNumber: ''
    };

    constructor (
        private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router, private authorizService: AuthorizationService,
        private authService: AuthenticationService,
        private toastr: ToastrService, private storageManagerService: StorageManagerService,
        private utilService: Utilities,
        private contactService: CompanyContactService, private sanitizer: DomSanitizer) { }

    // set the height of the popup element
    public ddwHeight = '220px';
    public ddwWdith = '240px';

    ngOnInit(): void {
        this.contactForm = this.fb.group({
            firstName: ['', { validators: [Validators.required, Validators.pattern(/^[^0-9]*$/)], updateOn: 'blur' }],
            lastName: ['', { validators: [Validators.required, Validators.pattern(/^[^0-9]*$/)], updateOn: 'blur' }],
            email: ['', { validators: [Validators.required, this.emailValidator], updateOn: 'blur' }],
            phoneNumber: ['', { validators: [Validators.required], updateOn: 'blur' }],
            address: ['', { updateOn: 'blur' }],
            city: ['', { updateOn: 'blur' }],
            countryId: ['', { updateOn: 'blur' }],
            countryRegion: ['', { updateOn: 'blur' }],
            zip: ['', { updateOn: 'blur' }],
            businessCategoryId: [''],
            phoneExt: ['', { validators: [Validators.pattern(/^[0-9]*$/)], updateOn: 'blur' }],
            dialCode: ['', { validators: [Validators.required, Validators.pattern(/^\+?[0-9-]*$/)], updateOn: 'blur' }],
            areaCode: ['', { validators: [Validators.required], updateOn: 'blur' }],
            status: ['Active', { validators: [Validators.required], updateOn: 'blur' }],
            saveAsDraft: ['']
        }, { validator: phoneNumberValidator('dialCode', 'areaCode', 'phoneNumber') });

        // Initialize some variables
        this.initParams();

        if (!this.contactRevisionId) {
            // Set contact status default to "Active"
            this.getFormControlByName('status').patchValue('Active');
            this.isFormDraftFlag = false;
            this.version = '';
            this.formStatus = '';
            this.selectedBusinessCategoryId = '';
            this.selectedCountryId = '';
            this.selectedRegionId = '';
        }

      // set focus on the invisible div so the firest tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
      this.initTabIndex();
    }

    initTabIndex() {
      (document.activeElement as HTMLElement).blur();

      const main = document.getElementById('firstDiv');

      if (main) {
        main.focus();
      }
    }

    // convenience getter for easy access to form fields
    get f() { return this.contactForm.controls; }

    // Check if user has write permission. If no, disable the "Update Contact" button
    checkUserPermission() {
        if (this.authorizService.hasPermission('SSDP.CanWriteContact')) {
            this.bHasPermission = true;
        }
        if (this.authorizService.hasPermission('SSDP.CanDeleteContact')) {
            this.bHasDeletePermission = true;
        }
    }

    // Get detail information for a specific contact
    getContactById(): void {

        if (this.contactRevisionId) {
            this.contactService.getContactById(this.contactRevisionId).subscribe(data => {
                if (data) {
                    this.countries = data['countries'];
                    this.regions = data['regions'];
                    this.contact = data['contactDetail'];
                    this.contactId = this.contact.contactId;
                    this.bIsDraftMode = this.contact.isDraftMode;
                    this.displayContact(this.contact);

                    this.contactRetrievedOutput.emit(this.contact);

                }
            },
              error => {
                this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
              }
            );
        }

    }

    // Retrieve list of all the business categoy
    getBusinessCategory(): void {
        this.contactService.getBusinessCategories(false).subscribe(data => {
            if (data) {
                this.businessCategories = data;
            }
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
    }

    // Build an ILLBusinessCategory object for Business Category
    getGenericKeyObjectForBC(list: any[], id: any): ILLBusinessCategory {
        const retVal = new ILLBusinessCategory();

        if (list && id) {
            list = list.filter(x => x['categoryId'] === id);
            if (list && list.length > 0) {
                retVal.categoryId = id && id !== '0' ? id : '';
                retVal.label = list[0]['label'];
            }
        }
        return retVal;
    }

    // Retrieve list of countries, default country to "United States"
    getCountries(): void {
        this.contactRevisionId = this.activatedRoute.snapshot.params.contactRevisionId;
        this.contactService.getCountries().subscribe(data => {
            if (data) {
                this.countries = data['countryList'];
            }
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
    }

    // Retrieve a list of Regions/States based on the country selected by the user
    getRegionsByCountry(countryCode: string): void {
        this.contactService.getRegionsByCountry(countryCode).subscribe(data => {
            if (data) {
                this.regions = data;
                // Pre select the drop down to the user's countryRegion
                this.selectedRegionId = this.contact && this.contact.countryRegion ? this.contact.countryRegion : '';
            }
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          }
        );
    }

    // Get the state/regions for the country selected
    countrySelectionChanged(event) {
        if (event.itemData) {
            this.getRegionsByCountry(event.itemData.id);
            this.contactForm.controls['countryRegion'].setValue('');
            if (!this.bFirstTime) {
                this.regionFromDrp = '';
                this.selectedRegionId = '';
            }
        } else {// selection being removed
            this.regions = [];
        }
        this.bFirstTime = false;
    }

    stateSelectionChanged(event) {
        this.regionFromDrp = '';
        if (event.itemData) {
            this.regionFromDrp = event.itemData.regionAndAbbreviation;
        }
    }

    // Populate the form
    displayContact(contact: IContactDetail): void {
        this.contactForm.reset();
        this.setcontactForm(contact);
    }

    public clearForm() {
        this.contactForm.reset();
    }

    // if the region code is not valid, set to empty
    validateRegion(regionCode: string) {
        if (this.regions) {
            const match = this.regions.filter(x => x.regionAndAbbreviation === regionCode);
            return match.length > 0 ? regionCode : '';
        } else {
            return '';
        }
    }

    // Save/Update contact info to the database
  saveContact(formValues): void {
    this.submitted = true;
    this.loading = true;
    this.errorMessage = '';
    const sessionCurrentCustomer = this.storageManagerService.read('currentCustomer');
    const currentCustomer = typeof sessionCurrentCustomer === 'object' ? sessionCurrentCustomer : JSON.parse(sessionCurrentCustomer);
    const authData = this.authService.getAuthData();

        // stop here if form is invalid
        if (this.contactForm.invalid) {
            this.loading = false;
            return;
        }

        if (this.contactForm.valid) {

            this.contactForm.markAsPristine();
            this.prepareDataForSave(formValues);

            this.contactService.createContact(formValues)
                .subscribe(data => {

                    this.loading = false;
                    this.bSavedContact = true;

                    if (!this.bFromCompanyPage) {
                        if (!this.bIsDraftMode) {
                            if (!this.parentComponentInput) {
                                this.router.navigate(['/contact-dashboard']);
                                return data;
                            } else {

                                if (this.parentComponentInput && (this.parentComponentInput instanceof LCRFormComponent || this.parentComponentInput instanceof ERFormComponent)) {
                                    this.contactForm.reset();
                                    this.contactOutput.emit(data);
                                }
                            }
                        } else {
                            // Force angular to rerun ngOnInit to reload data when navigating to address
                            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                            this.router.onSameUrlNavigation = 'reload';
                            this.router.navigate(['/contact', data]);
                        }
                    } else {
                        sessionStorage['ValidatedContactSave'] = 'true';
                        this.toastr.success('Contact saved successfully.');
                        // Update the version number on the form
                        this.getLatestContactVersion();
                    }
                    return data;
                },
                    error => {
                        this.loading = false;
                        this.toastr.error(error && error.errorMessage ? error.errorMessage : error);

                    }
                );

        }
    }

    // set the form as draft mode
    setDraftMode() {
        this.bIsDraftMode = true;
    }

    // set the form as active mode
    setActiveMode() {
        this.bIsDraftMode = false;
    }

    // Populate the contact form
    setcontactForm(contact: IContactDetail) {
        this.contactForm.patchValue({
            // firstName: this.sanitizedValue(contact.firstName),
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phoneNumber: contact.phoneNumber,
            address: contact.address,
            city: contact.city,
            zip: contact.zip,
            phoneExt: contact.phoneExtension,
            dialCode: contact.dialCode,
            areaCode: contact.areaCode,
            status: contact.loAFlag === true ? 'Inactive' : 'Active'
        });

        this.selectedCountryId = +this.contact.countryId === 0 || this.contact.countryId === null || this.contact.countryId === undefined ? '' : this.contact.countryId;
        this.selectedRegionId = this.contact.countryRegion ? this.contact.countryRegion : '';
        // this.selectedBusinessCategoryId = this.contact.businessCategoryId ? this.contact.businessCategoryId : '';
        this.selectedBusinessCategoryId = this.contact.businessCategory && this.contact.businessCategory.categoryId ? this.contact.businessCategory.categoryId : null;
        this.contactForm.patchValue({
            businessCategoryId: this.selectedBusinessCategoryId
        });

        this.getFormControlByName('countryId').setValue(this.selectedCountryId);
        // this.getFormControlByName('businessCategoryId').setValue(this.selectedBusinessCategoryId);
        this.getFormControlByName('countryRegion').setValue(this.selectedRegionId);
        this.regionFromDrp = this.selectedRegionId;
        this.isFormDraftFlag = this.contact.formActiveFlag === 'Active' ? false : true;
        this.version = this.contact.version;
        this.formStatus = 'Form status: ' + this.contact.formActiveFlag;
  }

  sanitizedValue(value): SafeHtml {

    return this.sanitizer.sanitize(SecurityContext.HTML, value);
  }

    // Get the object ready before being saved
    prepareDataForSave(formValues) {
        const sessionCurrentCustomer = this.storageManagerService.read('currentCustomer');
        const currentCustomer = typeof sessionCurrentCustomer === 'object' ? sessionCurrentCustomer : JSON.parse(sessionCurrentCustomer);
        const authData = this.authService.getAuthData();

        formValues['modifiedBy'] = authData.name;
        formValues['loAFlag'] = formValues['status'] === 'Inactive' ? true : false;
        formValues['IsDraftMode'] = this.bIsDraftMode === true ? true : false;
        // formValues['businessCategoryId'] = this.f.businessCategoryId.value === '' ? this.selectedBusinessCategoryId : this.f.businessCategoryId.value;
        // formValues['businessCategory'] = formValues['businessCategoryId'];
        formValues['businessCategory'] = this.getGenericKeyObjectForBC(this.businessCategories, this.f.businessCategoryId.value === '' ? this.selectedBusinessCategoryId : this.f.businessCategoryId.value);

        formValues['countryRegion'] = this.f.countryRegion.value === '' ? this.selectedRegionId : this.f.countryRegion.value;
        formValues['countryRegion'] = this.validateRegion(formValues['countryRegion']);
        formValues['countryId'] = this.f.countryId.value === '' ? this.selectedCountryId : this.f.countryId.value;
        formValues['country'] = formValues['countryId'];
        formValues['userId'] = currentCustomer.customerGuid;
        // if we are creating a new contact, we need to clear out the contactId
        if (this.contactRevisionId === null || this.contactRevisionId === undefined || this.contactRevisionId === '') {
            formValues['contactRevisionId'] = null;
            sessionStorage['currentContactId'] = null;
            formValues['parentVersion'] = '0.0';
        } else {
            formValues['contactRevisionId'] = this.contactRevisionId;
            formValues['contactId'] = this.contactId;
            formValues['parentVersion'] = this.contact.version;
        }
    }


    cancelBtnClicked() {

        if (!this.parentComponentInput) {

            this.router.navigateByUrl('/contact-dashboard');
        } else {
            this.contactForm.clearValidators();
            this.contactForm.reset();
            this.cancelButtonClickedOutput.emit();
        }
    }

    // Get a form control by name
    getFormControlByName(controlName: string): UntypedFormControl {
        return <UntypedFormControl>this.contactForm.get(controlName);
    }

    ngOnChanges(changes: SimpleChanges) {
        this.bFromCompanyPageEditLink = sessionStorage['fromCompanyPage'] === 'true' ? true : false;
        this.bFromCompanyPage = this.bFromCompanyPageEditLink;
        this.bSavedContact = false;

        for (const property in changes) {
            if (property === 'contactRevisionId') {
                if (this.contactRevisionId != undefined) {
                    this.buttonText = 'Update contact';
                    this.bFromCompanyPage = true;
                    this.selectedBusinessCategoryId = '';
                    this.selectedCountryId = '';
                    this.selectedRegionId = '';
                    this.regionFromDrp = '';
                    this.errorMessage = '';
                    this.getContactById();
                } else {
                    this.getBusinessCategory();
                    this.getCountries();
                }
            }
        }
        if (this.bFromCompanyPageEditLink) {
            this.pageTitle = this.contact.contactName;
            this.buttonText = 'Update contact';
            this.contactRevisionId = sessionStorage['currentContactRevisionId'];
            this.displayContact(this.contactData);
        }
    }

  isRequiredTypes(country: any): boolean {

    let retVal = false;

    if (this.companyType) {
      retVal = (this.companyType === this.COMPANY_TYPE_EST || this.companyType === this.COMPANY_TYPE_LABELER || this.companyType === this.COMPANY_TYPE_REGISTRANT) && country === +this.COUNTRY_USA;
    } else {
      retVal = country === +this.COUNTRY_USA;
    }
    return retVal;
  }

  // get the latest version number of the current contact
  getLatestContactVersion() {
      this.contactService.getContacts().subscribe(data => {
          if (data) {
              const currentContact = data['latestAvailableContactsList'].filter(x => x.contactId === +sessionStorage['ValidatedContactId']);
              if (currentContact && currentContact[0]) {
                  // this.version = currentContact[0].version.substring(0, currentContact[0].version.length - 1);
                  this.version = currentContact[0].version;
              }

          }
      },
        error => this.toastr.error(error.errorMessage)
      );
  }

    // Initialization
    initParams() {
        this.contactForm.reset();
        this.getBusinessCategory();
        this.getCountries();
        this.errorMessage = '';
        sessionStorage['fromCompanyPage'] = false;
        this.utilService.toggleClientDropdownState(true);
        this.checkUserPermission();
        this.errorMessage = '';
    }

    /**
     * Function to duplicate the Validators.Email
     * @param  {AbstractControl} control - the control to validate
     */
    emailValidator(control) {
        if (control.value) {
            const matches = control.value.match(/[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?/);
            return matches ? null : { 'invalidEmail': true };
        } else {
            return null;
        }
    }

    onSaveComplete(): void {
        this.contactForm.reset();
        this.router.navigate(['/conpany-contact-list']);
    }

    ngOnDestroy(): void {
        sessionStorage['fromCompanyPage'] = false;
        this.utilService.toggleClientDropdownState(false);
    }
}


